import {
    createEntityAdapter,
    createSelector,
    createSlice,
    EntityState,
    PayloadAction,
} from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { getDayBookingTypeItems } from "../../../services/dayBookingTypeService";
import { DayBookingType } from "../../../models/dayBookingType/dayBookingType";
import { DayBooking } from "../../../models/dayBooking/dayBooking";
import {
    deleteDayBooking,
    getDayBooking,
    getDayBookings,
    saveDayBooking,
} from "../../../services/dayBookingService";
import { DayBookingSaveData } from "../../../models/dayBooking/dayBookingSaveData";
import { Base } from "../../common/base";

export interface DayBookingState {
    dayBookingTypes: DayBookingType[];
}

const initialState: DayBookingState = {
    dayBookingTypes: null,
};

const dayBookingAdapter = createEntityAdapter<DayBooking>({
    // Compare by
    // 1. start day
    // 2. day usage type (Not full day first, then full day)
    // 3. Duration, shorter first
    sortComparer: (a, b) =>
        String(a.day).localeCompare(String(b.day)) ||
        b.dayUsageType - a.dayUsageType ||
        Math.floor(a.amount) - Math.floor(b.amount),
});

export const dayBookingSlice = createSlice({
    name: "dayBooking",
    initialState: dayBookingAdapter.getInitialState(initialState),
    reducers: {
        dayBookingsReceived: (state, action: PayloadAction<DayBooking[]>) => {
            dayBookingAdapter.upsertMany(state, action.payload);
        },
        dayBookingReceived: (state, action: PayloadAction<DayBooking>) => {
            dayBookingAdapter.upsertOne(state, action.payload);
        },
        dayBookingRemoved: (state, action: PayloadAction<string>) => {
            dayBookingAdapter.removeOne(state, action.payload);
        },
        setDayBookingTypes: (
            state,
            action: PayloadAction<DayBookingType[]>
        ) => {
            state.dayBookingTypes = action.payload;
        },
    },
});

export const {
    dayBookingsReceived,
    dayBookingReceived,
    dayBookingRemoved,
    setDayBookingTypes,
} = dayBookingSlice.actions;

export const fetchDayBookings = createAsyncThunk(
    "dayBooking/list",
    async(
        { startDate, endDate }: { startDate: string; endDate: string },
        thunkApi
    ) => {
        const dayBookings = await getDayBookings(
            startDate,
            endDate,
            thunkApi.signal
        );
        thunkApi.dispatch(dayBookingsReceived(dayBookings));
    }
);

export const fetchDayBooking = createAsyncThunk(
    "dayBooking/get",
    async(id: string, thunkApi) => {
        const dayBooking = await getDayBooking(id, thunkApi.signal);
        thunkApi.dispatch(dayBookingReceived(dayBooking));
    }
);

export const removeDayBooking = createAsyncThunk(
    "dayBooking/remove",
    async(dayBookingId: string, thunkApi) => {
        await deleteDayBooking(dayBookingId);
        thunkApi.dispatch(dayBookingRemoved(dayBookingId));
    }
);

export const fetchDayBookingTypes = createAsyncThunk(
    "dayBookingTypes",
    async(_, thunkApi) => {
        const dayBookingTypes = await getDayBookingTypeItems(thunkApi.signal);
        thunkApi.dispatch(setDayBookingTypes(dayBookingTypes));
    }
);

export const submitDayBooking = createAsyncThunk(
    "dayBooking/save",
    async(data: DayBookingSaveData, thunkApi) => {
        const dayBooking = await saveDayBooking(data);
        thunkApi.dispatch(dayBookingReceived(dayBooking));
    }
);

export const dayBookingReducer = dayBookingSlice.reducer;

export const dayBookingSelector = dayBookingAdapter.getSelectors();

export const makeSelecDayBookingsByDateRange = () =>
    createSelector(
        (state: EntityState<DayBooking, string> & DayBookingState) =>
            dayBookingSelector.selectAll(state),
        (_, rangeStart: string) => rangeStart,
        (_, _rangeStart, rangeEnd: string) => rangeEnd,
        (bookings: DayBooking[], rangeStart: string, rangeEnd: string) =>
            bookings.filter((b) =>
                Base.isOverlapping(b.day, b.endDay, rangeStart, rangeEnd, true)
            )
    );
